import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal, Tabs, Tag, Input, DatePicker, Row, Col, Typography } from 'antd';
import dayjs from 'dayjs';
import { FJ130, j203Api } from '../../services/j203Api';
import { BatchAcceptanceBridge, pwj203f1AcceptanceManifest } from '../../features/acceptance';
import LookupField from '../../components/LookupField';
import { useModulePermission } from '../../hooks/usePermission';
import { useJ203Mode } from './hooks/useJ203Mode';
import { useJ203Record } from './hooks/useJ203Record';
import { useJ203Navigation } from './hooks/useJ203Navigation';
import { useJ203LookupNames } from './hooks/useJ203LookupNames';
import { useJ203Approval } from './hooks/useJ203Approval';
import { useJ203Calculation } from './hooks/useJ203Calculation';
import { getApprovalBadge, isFieldDisabledFn } from './helpers';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Details from './components/Tab2Details';
import Tab3Extras from './components/Tab3Extras';
import { SharedActionBar } from '../../components/SharedActionBar';
import ApprovalHistoryModal from './components/ApprovalHistoryModal';
import './J203Form.css';

const MODULE_CODE = 'pwj203f1';

const J203Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('review');
  const [activeTab, setActiveTab] = useState('1');
  const permissions = useModulePermission(MODULE_CODE);

  // Hooks
  const { mode, setMode, isEditing } = useJ203Mode();
  const { names, resolveAllNames, resolveSingleName, clearNames } = useJ203LookupNames();
  const {
    record, formData, detailTables, loading,
    loadRecord, setField, getField,
    setDetailTable,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
  } = useJ203Record();
  const { recalcAll } = useJ203Calculation();

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (id: string) => {
    const data = await loadRecord(id);
    if (data) resolveAllNames(data);
    return data;
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useJ203Navigation(
    useCallback(async (id: string) => { await loadRecordWithNames(id); }, [loadRecordWithNames]),
  );

  // Approval hook
  const {
    handleSubmit, handleApprove, handleReject, handleWriteoff, handleViewHistory,
    historyVisible, setHistoryVisible, historyData,
  } = useJ203Approval({
    record, isEditing,
    loadRecord: loadRecordWithNames,
  });

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (reviewId) {
          const idx = ids.indexOf(reviewId);
          if (idx >= 0) setCurrentIdx(idx);
          await loadRecordWithNames(reviewId);
        } else if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        console.error('Failed to load J203 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // isFieldDisabled helper
  const isFieldDisabled = useCallback(
    (key: string) => isFieldDisabledFn(key, isEditing, mode),
    [isEditing, mode],
  );

  // Mode Actions
  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      await handleSave(mode, async (result: FJ130) => {
        if (mode === 'add') {
          const ids = await loadList();
          const newIdx = ids.indexOf(result.fj130_01);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        resolveAllNames(result);
        setMode('browse');
      });
    } else {
      setMode('add');
      resetForAdd();
      clearNames();
      setActiveTab('1');
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, resolveAllNames, setMode, resetForAdd, clearNames]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      if (record) resolveAllNames(record);
      setMode('browse');
    } else {
      if (!record) return;
      if (record.fj130_80 === 'P') {
        Modal.warning({ title: '提示', content: '已核准的記錄不可修改' });
        return;
      }
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, resolveAllNames, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    if (record.fj130_80 === 'P') {
      Modal.warning({ title: '提示', content: '已核准的記錄不可刪除' });
      return;
    }
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除交運單 ${record.fj130_01}？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const nextIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(nextIdx);
            await loadRecordWithNames(ids[nextIdx]);
          } else {
            resetForAdd();
            clearNames();
            setCurrentIdx(-1);
          }
        }
      },
    });
  }, [record, isEditing, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, resetForAdd, clearNames]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  // Status badge
  const statusBadge = getApprovalBadge(formData.fj130_80 as string || 'N');

  // Tab items
  const tabItems = [
    {
      key: '1',
      label: '基本資料',
      children: (
        <Tab1BasicInfo
          formData={formData}
          names={names}
          isEditing={isEditing}
          isFieldDisabled={isFieldDisabled}
          getField={getField}
          setField={setField}
          resolveSingleName={resolveSingleName}
        />
      ),
    },
    {
      key: '2',
      label: '明細',
      children: (
        <Tab2Details
          rows={detailTables.details}
          isEditing={isEditing}
          totalItems={(formData.fj130_26 as number) || detailTables.details.length}
          onRowsChange={updater => setDetailTable('details', updater)}
        />
      ),
    },
    {
      key: '3',
      label: '帳款/發票',
      children: (
        <Tab3Extras
          rows={detailTables.extras}
          paymentRows={detailTables.payments}
          isEditing={isEditing}
          onRowsChange={updater => setDetailTable('extras', updater)}
          onPaymentRowsChange={updater => setDetailTable('payments', updater)}
        />
      ),
    },
  ];

  return (
    <div className="j203-form">
      {loading && <div className="loading-overlay">載入中...</div>}
      <div className="page-wrapper">
        {/* Page Title */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <span className="current">樣品交運單</span>
            </nav>
            <span className="title-separator">--</span>
            <h1>樣品交運單</h1>
            <span className="badge badge-default">J203</span>
            <Tag color={statusBadge.color} style={{ marginLeft: 8 }}>{statusBadge.label}</Tag>
          </div>
        </div>

        {/* Record info bar */}
        <div className="record-info-bar">
          <span>
            <span className="info-label">單號:</span>
            <span className="info-value">{formData.fj130_01 || '(新增)'}</span>
          </span>
          <span>
            <span className="info-label">記錄:</span>
            <span className="info-value">
              {allIds.length > 0 ? `${currentIdx + 1} / ${allIds.length}` : '0 / 0'}
            </span>
          </span>
          <span>
            <span className="info-label">模式:</span>
            <span className="info-value">
              {mode === 'browse' ? '瀏覽' : mode === 'add' ? '新增' : '編輯'}
            </span>
          </span>
        </div>

        {/* Form-level fields (above tabs, per FoxPro layout) */}
        <div className="form-level-fields">
          <Row gutter={[16, 8]} align="middle">
            <Col>
              <Typography.Text type="secondary">樣品交運單號</Typography.Text>
            </Col>
            <Col>
              <Input size="small" value={formData.fj130_01 as string || ''} disabled style={{ width: 120 }} />
            </Col>
            <Col>
              <Typography.Text type="secondary">出貨通知單號</Typography.Text>
            </Col>
            <Col>
              <LookupField
                lookupType="shippingNotice"
                value={(formData.fj130_52 as string) || ''}
                onChange={(code) => setField('fj130_52', code)}
                disabled={isFieldDisabled('fj130_52')}
                style={{ width: 140 }}
                title="搜尋出貨通知單" contractFieldId="fj130_52"
              />
            </Col>
            <Col>
              <Typography.Text type="secondary">交運日期</Typography.Text>
            </Col>
            <Col>
              <DatePicker size="small" disabled={isFieldDisabled('fj130_11')}
                value={formData.fj130_11 ? dayjs(formData.fj130_11 as string) : null}
                onChange={d => setField('fj130_11', d ? d.toISOString() : null as unknown as string)}
                style={{ width: 160 }}
              />
            </Col>
          </Row>
        </div>

        {/* Tabs */}
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} type="card" size="small" />

        {/* Action Bar */}
        <SharedActionBar
          mode={mode}
          isEditing={isEditing}
          hasRecord={!!record}
          currentIdx={currentIdx}
          recordCount={allIds.length}
          onNavFirst={() => handleNavTo(0)}
          onNavPrev={() => handleNavTo(currentIdx - 1)}
          onNavNext={() => handleNavTo(currentIdx + 1)}
          onNavLast={() => handleNavTo(allIds.length - 1)}
          onAdd={handleAddAction}
          onModify={handleModifyAction}
          onDelete={handleDeleteAction}
          approval={{
            statusCode: (formData.fj130_80 as string) || '',
            isApproved: formData.fj130_80 === 'P',
            isPending: ['D', 'S'].includes((formData.fj130_80 as string) || ''), // 統一碼 D + 舊碼 S 過渡容忍
            isLocked: ['P', 'D', 'S'].includes((formData.fj130_80 as string) || ''),
          }}
          onSubmit={handleSubmit}
          onApprove={handleApprove}
          onReject={handleReject}
          onWriteoff={handleWriteoff}
          onHistory={handleViewHistory}
          onExit={() => navigate('/erp')}
          canCreate={permissions.canCreate}
          canEdit={permissions.canEdit}
          canDelete={permissions.canDelete}
          canApprove={permissions.canApprove}
        />
        <BatchAcceptanceBridge
          manifest={pwj203f1AcceptanceManifest}
          records={record ? [record] : []}
          selectedKeys={record?.fj130_01 ? [record.fj130_01] : []}
          keyOf={(item) => item.fj130_01}
          queryExact={(key) => j203Api.get(key)}
          lastApiResponse={record}
          loading={loading}
          onAutoFill={() => allIds[0] && handleNavTo(0)}
          autoFillDisabled={allIds.length === 0}
          screenDraft={{ master: formData, details: detailTables }}
        />
      </div>

      {/* Approval History Modal */}
      <ApprovalHistoryModal
        visible={historyVisible}
        onClose={() => setHistoryVisible(false)}
        data={historyData}
      />
    </div>
  );
};

export default J203Form;
